import { inject, injectable } from 'inversify'
import { RRuleFrequency, type RRuleOptions } from '@/services/rrule/types'
import type { HoursIntervalType, RRulestrByWeekday, WeekDaysFull } from '@/services/types'
import { DATE_FORMATES, weekDaysFullVariant, weekDaysStartingMonday } from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type { ByWeekday, RRuleService, Weekday } from '@/services/rrule/rruleService'
import type { RepeatData, RepeatType } from '@/services/domain/scheduled/types'
import type { ComposeSchedule } from '@/services/forms/baseScheduleFormService'
import type TimezoneService from '@/services/domain/timezone/timezoneService'
import type DateTimeService from '@/services/dateTimeService'
import type UserService from '@/services/domain/user/userService'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import TmLogicError from '@/core/error/tmLogicError'
import type ComposeScheduledInitFormData from '@/services/domain/compose/ComposeScheduledInitFormData'

const frequencyMap: Partial<{ [key in RRuleFrequency]: RepeatType }> = {
  [RRuleFrequency.HOURLY]: 'hourly',
  [RRuleFrequency.DAILY]: 'daily',
  [RRuleFrequency.WEEKLY]: 'weekly',
  [RRuleFrequency.MONTHLY]: 'monthly',
  [RRuleFrequency.YEARLY]: 'yearly',
}

@injectable()
export class ScheduledMapperService {
  constructor(
    @inject(SERVICE_TYPES.RRuleService) protected readonly rruleService: RRuleService,
    @inject(SERVICE_TYPES.TimezoneService) protected readonly timezoneService: TimezoneService,
    @inject(SERVICE_TYPES.DateTimeService) protected readonly dateTimeService: DateTimeService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.ComposeScheduledInitFormData)
    protected readonly composeScheduledInitFormData: ComposeScheduledInitFormData,
  ) {}

  public getComposeSchedule(nextSend: string, timezoneName: string, rrule: string | null): ComposeSchedule {
    const currentUser = this.userService.currentUser(['timezone'])

    const timezone = this.timezoneService.getEntityByTimezone(timezoneName)
    const is12hTimeFormat = currentUser.displayTimeFormat === '12h'

    const fullDate = this.dateTimeService.utcToZonedTime(this.dateTimeService.toDate(nextSend), timezone?.timezone)
    let hours = fullDate.getHours()
    const minutes = fullDate.getMinutes()

    let timeIntervalType: HoursIntervalType = 'AM'
    if (is12hTimeFormat) {
      const { hours: hours12hFormat, type } = this.dateTimeService.hoursTo12hFormat(hours)
      hours = hours12hFormat
      timeIntervalType = type
    }

    const schedule: ComposeSchedule = {
      sendingTimezone: timezone?.id ?? currentUser.timezone.id,
      sendingDateTime: {
        date: this.dateTimeService.toISO(this.dateTimeService.zonedTimeToUtc(fullDate, currentUser.timezone.timezone)),
        hours,
        minutes,
        timeIntervalType,
      },
      repeat: this.composeScheduledInitFormData.getMessageSendRepeatInitFormData(),
    }

    if (!rrule) {
      return schedule
    }

    const repeatData = this.getScheduleRepeatFromRule(rrule)

    schedule.repeat.type = repeatData.freqType

    if (repeatData.endType) {
      schedule.repeat.end.type = repeatData.endType

      if (repeatData.smsSessions) {
        schedule.repeat.end.smsSessions = repeatData.smsSessions
      } else if (repeatData.until) {
        schedule.repeat.end.date = repeatData.until
      }
    }
    if (repeatData.freqType === 'hourly') {
      schedule.repeat.hourly = {
        count: repeatData.count ?? schedule.repeat.hourly.count,
      }
    } else if (repeatData.freqType === 'daily') {
      schedule.repeat.daily = {
        count: repeatData.count ?? schedule.repeat.daily.count,
      }
    } else if (repeatData.freqType === 'weekly') {
      schedule.repeat.weekly = {
        count: repeatData.count ?? schedule.repeat.weekly.count,
        days: repeatData.days ?? schedule.repeat.weekly.days,
      }
    } else if (repeatData.freqType === 'monthly') {
      schedule.repeat.monthly = {
        count: repeatData.count ?? schedule.repeat.monthly.count,
        firstLastDay: repeatData.firstLastDay ?? schedule.repeat.monthly.firstLastDay,
        monthDay: repeatData.monthDay ?? schedule.repeat.monthly.monthDay,
        repeat: repeatData.repeat ?? schedule.repeat.monthly.repeat,
        weekDay: repeatData.weekDay ?? schedule.repeat.monthly.weekDay,
      }
    } else if (repeatData.freqType === 'yearly') {
      schedule.repeat.yearly = {
        count: repeatData.count ?? schedule.repeat.yearly.count,
        dayOfMonth: repeatData.dayOfMonth ?? schedule.repeat.yearly.dayOfMonth,
        firstLastDay: repeatData.firstLastDay ?? schedule.repeat.yearly.firstLastDay,
        month: repeatData.month ?? schedule.repeat.yearly.month,
        monthDay: repeatData.monthDay ?? schedule.repeat.yearly.monthDay,
        type: repeatData.type ?? schedule.repeat.yearly.type,
        weekDay: repeatData.weekDay ?? schedule.repeat.yearly.weekDay,
      }
    } else {
      throw new TmAbsurdError(repeatData.freqType)
    }

    return schedule
  }

  private getEndDateFieldValue(until: Date): string {
    const timezone = this.userService.getCurrentTimezone()?.timezone
    const endDay = this.dateTimeService.getDateFromStringFormat(
      until.toISOString().slice(0, 10),
      DATE_FORMATES.DATE_ONLY_FORMAT,
    )

    return this.dateTimeService.toISO(this.dateTimeService.zonedTimeToUtc(endDay, timezone))
  }

  private getScheduleRepeatFromRule(rrule: string): RepeatData {
    const rruleOptions = this.rruleService.getInstanceFromString(rrule).origOptions
    const data: RepeatData = {
      freqType: this.getRepeatType(rruleOptions.freq!),
    }

    if (['hourly', 'daily', 'weekly', 'monthly'].includes(data.freqType)) {
      data.count = rruleOptions.interval
    }
    if (data.freqType === 'weekly') {
      data.days = this.getDays(rruleOptions.byweekday)
    }
    if (rruleOptions.count) {
      data.endType = 'after'
      data.smsSessions = rruleOptions.count
    }
    if (rruleOptions.until) {
      data.endType = 'date'
      data.until = this.getEndDateFieldValue(rruleOptions.until)
    }
    if (data.freqType === 'monthly') {
      return {
        ...data,
        ...this.getMonthlyData(rruleOptions),
      }
    }
    if (data.freqType === 'yearly') {
      return {
        ...data,
        ...this.getYearlyData(rruleOptions),
      }
    }

    return data
  }

  private getDays(byweekday: ByWeekday | ByWeekday[] | null | undefined) {
    if (!byweekday) {
      return []
    }
    if (!Array.isArray(byweekday)) {
      throw new TmLogicError('byweekday should be type Array')
    }
    return byweekday.map((el) => weekDaysStartingMonday[(el as Weekday).weekday])
  }

  protected getRepeatType(freq: RRuleFrequency): RepeatType {
    return frequencyMap[freq] ?? 'hourly'
  }

  protected getMonthlyData(rruleOptions: Partial<RRuleOptions>) {
    if (typeof rruleOptions.bymonthday === 'number') {
      return {
        repeat: 'month-day',
        monthDay: rruleOptions.bymonthday.toString(),
      } as const
    }

    if (this.isMonthFloatDayRule(rruleOptions)) {
      const byweekday = (rruleOptions.byweekday as RRulestrByWeekday[]).map((el) => weekDaysFullVariant[el.weekday])
      return {
        repeat: 'month-float-day',
        firstLastDay: this.rruleService.getOrdinalDayBysetpos(rruleOptions.bysetpos as number),
        weekDay: this.calculateWeekDay(byweekday),
      } as const
    }

    return undefined
  }

  protected getYearlyData(rruleOptions: Partial<RRuleOptions>) {
    if (this.isYearDayRule(rruleOptions)) {
      return {
        type: 'year-day',
        month: this.rruleService.getMonthByMonthNumber(rruleOptions.bymonth as number),
        monthDay: (rruleOptions.bymonthday as number).toString(),
      } as const
    }
    if (this.isYearFloatDayRule(rruleOptions)) {
      const byweekday = (rruleOptions.byweekday as RRulestrByWeekday[]).map((el) => weekDaysFullVariant[el.weekday])
      return {
        type: 'year-float-day',
        firstLastDay: this.rruleService.getOrdinalDayBysetpos(rruleOptions.bysetpos as number),
        dayOfMonth: this.rruleService.getMonthByMonthNumber(rruleOptions.bymonth as number),
        weekDay: this.calculateWeekDay(byweekday),
      } as const
    }

    return false
  }

  protected isMonthFloatDayRule(rruleOptions: Partial<RRuleOptions>) {
    return (
      rruleOptions.bysetpos &&
      typeof rruleOptions.bysetpos === 'number' &&
      Array.isArray(rruleOptions.byweekday) &&
      rruleOptions.byweekday.length
    )
  }

  protected isYearDayRule(rruleOptions: Partial<RRuleOptions>) {
    return typeof rruleOptions.bymonth === 'number' && typeof rruleOptions.bymonthday === 'number'
  }

  protected isYearFloatDayRule(rruleOptions: Partial<RRuleOptions>) {
    return (
      rruleOptions.bysetpos &&
      typeof rruleOptions.bysetpos === 'number' &&
      rruleOptions.bymonth &&
      typeof rruleOptions.bymonth === 'number'
    )
  }

  protected calculateWeekDay(byweekday: WeekDaysFull[]): WeekDaysFull {
    if (byweekday.length === 7) return weekDaysFullVariant[7]
    if (byweekday.length === 5) return weekDaysFullVariant[8]
    if (byweekday.length === 2) return weekDaysFullVariant[9]
    return byweekday[0]
  }
}
